import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map, timeout, tap } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { LoggerService } from 'src/services/logger.service';
import { StorageService } from 'src/services/storage.service';
import { Properties } from '../app-config/properties';
import { USERROLE } from 'src/util/constant';
import { v4 as uuidv4 } from 'uuid';

@Injectable({
  providedIn: 'root'
})
export class UserManagementService {

  constructor(
    private loggerService: LoggerService,
    private http: HttpClient,
    private storageService: StorageService
  ) { }

  authenticateUserAccount(body: any, params?: HttpParams): Observable<any> {
    return this.http
      .post(Properties.AUTH_USER_ENDPOINT, body, {
        params: params
      })
      .pipe(catchError(this.handleError));
  }

  authorizeUserAccount(body: any, params?: HttpParams): Observable<any> {
    return this.http
      .post(Properties.AUTH_USER_ENDPOINT, body, {
        params: params
      })
      .pipe(catchError(this.handleError));
  }

  handleError(error: HttpErrorResponse) {
    let errorMessage = 'Unknown error';
    if (error.error instanceof ErrorEvent) {
      errorMessage = error.error.message;
    } else {
      errorMessage = `Error: ${error.status} \n Message: ${error.message}`;
    }

    return throwError(errorMessage);
  }

  getUsersServiceRequest(body: any, params?: HttpParams): Observable<any> {
    return this.http
      .post(Properties.GET_ADMIN_USER_SERVICE_REQUEST, body, {
        params: params
      })
      .pipe(catchError(this.handleError));
  }

  verifyUserAccount(body: any, params?: HttpParams): Observable<any> {
    return this.http
      .post(Properties.VERIFY_USER_ACCOUNT, body, {
        params: params
      })
      .pipe(catchError(this.handleError));
  }

  validateUserAccountSetupLink(body: any, params?: HttpParams): Observable<any> {
    return this.http
      .post(Properties.VALIDATE_USER_ACCOUNT_SETUP_LINK, body, {
        params: params
      })
      .pipe(catchError(this.handleError));
  }

  completeUserAccountSetup(body: any, params?: HttpParams): Observable<any> {
    return this.http
      .post(Properties.COMPLETE_USER_ACCOUNT_SETUP, body, {
        params: params
      })
      .pipe(catchError(this.handleError));
  }

  createUserServiceRequest(body: any, params?: HttpParams): Observable<any> {
    return this.http
      .post(Properties.CREATE_ADMIN_USER, body, {
        params: params
      })
      .pipe(catchError(this.handleError));
  }

  editUserServiceRequest(body: any, params?: HttpParams): Observable<any> {
    return this.http
      .post(Properties.EDIT_ADMIN_USER, body, {
        params: params
      })
      .pipe(catchError(this.handleError));
  }

  deleteUserAccountServiceRequest(body: any, params?: HttpParams): Observable<any> {
    return this.http
      .post(Properties.DELETE_ADMIN_USER_ACCOUNT, body, {
        params: params
      })
      .pipe(catchError(this.handleError));
  }

  resetUserPasswordServiceRequest(body: any, params?: HttpParams): Observable<any> {
    return this.http
      .post(Properties.RESET_ADMIN_USER_PASSWORD, body, {
        params: params
      })
      .pipe(catchError(this.handleError));
  }

  getOrganizationListServiceRequest(): Observable<any> {
    return this.http.get(Properties.GET_ORGANIZATION_LIST_SERVICE_REQUEST)
      .pipe(catchError(this.handleError));
  }

  getIDTemplateListServiceRequest(): Observable<any> {
    return this.http.get(Properties.GET_ID_TEMPLATE_LIST_SERVICE_REQUEST)
      .pipe(catchError(this.handleError));
  }

  getIdDetails(userId: string): Observable<any> {
    return this.http.get(Properties.GET_PROJECTS_DETAILS_SERVICE_REQUEST + `/${userId}`)
      .pipe(catchError(this.handleError));
  }

  deactivateUser(body: any, params?: HttpParams): Observable<any> {
    return this.http.post(Properties.DEACTIVATE_PROJECT_SERVICE_REQUEST, body, {
      params: params
    })
      .pipe(catchError(this.handleError));
  }

  getBioUserUsers() {
    let email = this.storageService.getUserEmail()
    let token = this.storageService.getAuthUserToken();
    return this.http.get(`${Properties.GET_USER_PROJECTS}?email=${email}&authToken=${token}`).pipe(catchError(this.handleError))
  }

  startImportingData(pid: string) {
    let email = this.storageService.getUserEmail()
    return this.http.post(`${Properties.IMPORT_DATA_FROM_FORM}?email=${email}&pId=${pid}`, {}).pipe(catchError(this.handleError))
  }

  generatePassword(length = 12, options = {}): string {
    const defaultOptions = {
      numbers: true,
      symbols: true,
      uppercase: true,
      lowercase: true,
    };

    const opts = { ...defaultOptions, ...options };

    const lowerChars = 'abcdefghijklmnopqrstuvwxyz';
    const upperChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numberChars = '0123456789';
    const symbolChars = '@$!%*?&'; //'!@#$%^&*()_+[]{}|;:,.<>?';

    let charPool = '';

    if (opts.lowercase) charPool += lowerChars;
    if (opts.uppercase) charPool += upperChars;
    if (opts.numbers) charPool += numberChars;
    if (opts.symbols) charPool += symbolChars;

    if (charPool.length === 0) {
      throw new Error('At least one character type must be selected.');
    }

    let password = '';
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charPool.length);
      password += charPool[randomIndex];
    }

    return password;
  }

  generateSession() {
    const sessionId = uuidv4();
    return {
      sessionId
    }
  }

  getUserRoles() {
    return {
      placeholder: 'User Role',
      items: [
        {
          displayText: 'Super Admin',
          value: USERROLE.SUPERADMIN,
          i18nKey: 'superAdminRole',
        },
        {
          displayText: 'Admin',
          value: USERROLE.ADMIN,
          i18nKey: 'adminRoleKey',
        },
        {
          displayText: 'User',
          value: USERROLE.USER,
          i18nKey: 'userRoleKey',
        },
        {
          displayText: 'Printer',
          value: USERROLE.PRINTER,
          i18nKey: 'printerRoleKey',
        },
      ],
      i18nKey: "userRoleKey",
      paramKey: 'userRoleKey',
    };
  }
}
